import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSupabaseClient, useSession } from '@supabase/auth-helpers-react';
import { useData } from '../../../../hooks/useData';
import { TABLES } from '../../../../constants/supabaseConstants';
import { autoCreateStaircaseSpace } from '../../../../utils/autoProjectItems';
import modalStyles from '../../../../styles/modals.module.css';

const getFloorLabel = num => {
  if (num === -1) return 'קומת מרתף';
  if (num === 0) return 'קומת קרקע';
  if (num === 1) return 'קומה ראשונה';
  if (num === 2) return 'קומה שנייה';
  if (num === 3) return 'קומה שלישית';
  if (num === 4) return 'קומה רביעית';
  return 'קומה חמישית';
};

const DEFAULT_FLOOR_VALUES = {
  floor_number: '',
  floor_area: 0,
  floor_type: 'new',
  floor_height: 2.5, // Standard floor height in meters
  ceiling_style: 'flat',
  ceiling_material: 'concrete',
  stairs_to_floor: '',
};

const FloorFormModal = ({ open, onClose, floor = null, onSubmitRef, data, allFloors }) => {
  const currentProject = data?.currentProject;
  const supabase = useSupabaseClient();
  const session = useSession();

  const [formData, setFormData] = useState(() => ({
    ...DEFAULT_FLOOR_VALUES,
    ...(floor || {}),
  }));

  const [errors, setErrors] = useState({});
  const [floorChoices, setFloorChoices] = useState({}); // masterItemId → selected child id

  const { manageFloors } = useData(TABLES.PROJECT_FLOORS, {
    enabled: !!currentProject?.id,
    filter: { project_id: currentProject?.id },
  });

  // Fetch floor-specific home choice items (choice bundles with scope=floor_specific)
  const { data: floorChoiceItems } = useData(TABLES.REFERENCE_HOME_CHOICE_ITEMS, {
    select: `*, master_item:master_items(id, item_name, bundle_type, bundle_items)`,
    filter: { scope: 'floor_specific' },
    enabled: open,
    realtime: false,
  });

  // Fetch bundle children for choice bundles
  const choiceBundleChildIds = useMemo(() => {
    if (!floorChoiceItems) return [];
    const ids = [];
    floorChoiceItems.forEach(item => {
      if (item.master_item?.bundle_type === 'choice' && item.master_item?.bundle_items?.items) {
        item.master_item.bundle_items.items.forEach(child => ids.push(child.item_id));
      }
    });
    return [...new Set(ids)];
  }, [floorChoiceItems]);

  const { data: bundleChildren } = useData(TABLES.MASTER_ITEMS, {
    select: 'id, item_name',
    filter: { id: choiceBundleChildIds },
    enabled: choiceBundleChildIds.length > 0,
    realtime: false,
  });

  // Fetch existing defaults for this floor (edit mode)
  const { data: existingDefaults } = useData(TABLES.PROJECT_DEFAULT_OPTIONS, {
    select: 'master_item_id, selected_option',
    filter: { project_id: currentProject?.id, floor_id: floor?.id },
    enabled: !!floor?.id && !!currentProject?.id,
    realtime: false,
  });

  // Pre-populate choices from existing defaults when editing
  useEffect(() => {
    if (existingDefaults && floor?.id) {
      const choices = {};
      existingDefaults.forEach(d => {
        if (d.selected_option?.selected_child_id) {
          choices[d.master_item_id] = d.selected_option.selected_child_id;
        }
      });
      setFloorChoices(choices);
    }
  }, [existingDefaults, floor?.id]);

  // Build choice options per item
  const choiceItemsWithChildren = useMemo(() => {
    if (!floorChoiceItems || !bundleChildren) return [];
    return floorChoiceItems
      .filter(item => item.master_item?.bundle_type === 'choice')
      .map(item => {
        const children = (item.master_item.bundle_items?.items || [])
          .map(child => bundleChildren.find(bc => bc.id === child.item_id))
          .filter(Boolean);
        return {
          masterItemId: item.master_item_id,
          name: item.master_item.item_name,
          children,
        };
      });
  }, [floorChoiceItems, bundleChildren]);

  const allNumbers = Array.from({ length: 7 }, (_, i) => i - 1);
  const usedNumbers = new Set(allFloors?.map(f => f.floor_number) || []);

  if (floor?.floor_number !== undefined) {
    usedNumbers.delete(floor.floor_number);
  }

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const businessErrors = {};

    if (formData.floor_number === '') {
      businessErrors.floor_number = 'מספר קומה נדרש';
    }

    const isDuplicateNumber = allFloors?.some(
      f => f.floor_number === Number(formData.floor_number) && !floor && f.id !== floor.id,
    );

    if (isDuplicateNumber) {
      businessErrors.floor_number = 'מספר קומה כבר קיים';
    }

    if (formData.floor_height <= 0) {
      businessErrors.floor_height = 'גובה קומה חייב להיות גדול מ-0';
    }

    if (formData.stairs_to_floor !== '' && Number(formData.stairs_to_floor) < 0) {
      businessErrors.stairs_to_floor = 'מספר מדרגות חייב להיות 0 או חיובי';
    }

    return Object.keys(businessErrors).length > 0 ? businessErrors : null;
  };

  const handleSubmit = async e => {
    if (e) e.preventDefault();

    const validationErrors = validateForm();
    if (validationErrors) {
      setErrors(validationErrors);
      return;
    }

    try {
      const floorData = {
        ...formData,
        floor_number: Number(formData.floor_number),
        floor_area: Number(formData.floor_area),
        floor_height: Number(formData.floor_height),
        stairs_to_floor: formData.stairs_to_floor !== '' ? Number(formData.stairs_to_floor) : null,
        project_id: currentProject.id,
      };

      if (floor?.id) {
        floorData.id = floor.id;
      }

      await manageFloors([floorData]);

      // Auto-create staircase space if floor has stairs
      const stairsCount = floorData.stairs_to_floor;
      if (stairsCount && stairsCount > 0 && currentProject?.id) {
        try {
          // For existing floors, use floor.id; for new floors, fetch the DB ID
          let dbFloorId = floor?.id;
          if (!dbFloorId) {
            const { data: newFloor } = await supabase
              .from('project_floors')
              .select('id')
              .eq('project_id', currentProject.id)
              .eq('floor_number', floorData.floor_number)
              .single();
            dbFloorId = newFloor?.id;
          }
          if (dbFloorId) {
            await autoCreateStaircaseSpace(supabase, {
              projectId: currentProject.id,
              floorId: dbFloorId,
              stairCount: stairsCount,
            });
          }
        } catch (err) {
          console.error('Failed to auto-create staircase space:', err);
        }
      }

      // Save floor-specific choice defaults
      if (Object.keys(floorChoices).length > 0 && currentProject?.id) {
        try {
          let dbFloorId = floor?.id;
          if (!dbFloorId) {
            const { data: newFloor } = await supabase
              .from('project_floors')
              .select('id')
              .eq('project_id', currentProject.id)
              .eq('floor_number', Number(formData.floor_number))
              .single();
            dbFloorId = newFloor?.id;
          }

          if (dbFloorId) {
            for (const [masterItemId, childId] of Object.entries(floorChoices)) {
              if (!childId) continue;
              // Check if record exists
              const { data: existing } = await supabase
                .from('project_default_options')
                .select('id')
                .eq('project_id', currentProject.id)
                .eq('master_item_id', masterItemId)
                .eq('floor_id', dbFloorId)
                .is('master_space_id', null)
                .maybeSingle();

              const optionData = { selected_child_id: childId };
              if (existing) {
                await supabase
                  .from('project_default_options')
                  .update({
                    selected_option: optionData,
                    updated_by: session?.user?.id,
                  })
                  .eq('id', existing.id);
              } else {
                await supabase.from('project_default_options').insert({
                  project_id: currentProject.id,
                  master_item_id: masterItemId,
                  floor_id: dbFloorId,
                  selected_option: optionData,
                  created_by: session?.user?.id,
                  updated_by: session?.user?.id,
                });
              }
            }
          }
        } catch (err) {
          console.error('Failed to save floor choices:', err);
        }
      }

      onClose();
    } catch (error) {
      console.error('Error submitting floor:', error);
      if (error.message === 'Floor validation failed') {
        setErrors(error.cause);
      } else {
        alert('שגיאה בשמירת הקומה');
      }
    }
  };

  useEffect(() => {
    if (open && onSubmitRef) {
      onSubmitRef(() => handleSubmit);
    }
  }, [open, onSubmitRef]);

  if (!open) return null;

  return createPortal(
    <div className={modalStyles.overlay} onClick={onClose}>
      <div className={modalStyles.modal} dir="rtl" onClick={e => e.stopPropagation()}>
        <div className={modalStyles.header}>
          <h2 className={modalStyles.headerTitle}>{floor ? 'עריכת קומה' : 'הוספת קומה חדשה'}</h2>
        </div>

        <div className={modalStyles.content}>
          {errors.submit && <div className={modalStyles.error}>{errors.submit}</div>}

          <div className={modalStyles.formGrid}>
            <div className={modalStyles.field}>
              <label className={modalStyles.fieldLabel}>
                <span className={modalStyles.fieldRequired}>*</span>
                מספר קומה
              </label>
              <select
                className={modalStyles.fieldSelect}
                name="floor_number"
                value={formData.floor_number}
                onChange={handleChange}
              >
                <option value="">בחר קומה</option>
                {allNumbers.map(num => {
                  const isUsed = usedNumbers.has(num);
                  if (isUsed && num !== floor?.floor_number) {
                    return (
                      <option key={num} value={num} disabled>
                        {getFloorLabel(num)} (כבר בשימוש בפרויקט)
                      </option>
                    );
                  }
                  return (
                    <option key={num} value={num}>
                      {getFloorLabel(num)}
                    </option>
                  );
                })}
              </select>
              {errors.floor_number && (
                <div className={modalStyles.fieldError}>{errors.floor_number}</div>
              )}
            </div>

            <div className={modalStyles.formRow}>
              <div className={modalStyles.field}>
                <label className={modalStyles.fieldLabel}>שטח הקומה</label>
                <input
                  type="number"
                  className={modalStyles.fieldInput}
                  name="floor_area"
                  value={formData.floor_area}
                  onChange={handleChange}
                />
                {errors.floor_area && (
                  <div className={modalStyles.fieldError}>{errors.floor_area}</div>
                )}
              </div>

              <div className={modalStyles.field}>
                <label className={modalStyles.fieldLabel}>גובה הקומה</label>
                <input
                  type="number"
                  className={modalStyles.fieldInput}
                  name="floor_height"
                  value={formData.floor_height}
                  onChange={handleChange}
                />
                {errors.floor_height && (
                  <div className={modalStyles.fieldError}>{errors.floor_height}</div>
                )}
              </div>
            </div>

            <div className={modalStyles.formRow}>
              <div className={modalStyles.field}>
                <label className={modalStyles.fieldLabel}>סוג הקומה</label>
                <select
                  className={modalStyles.fieldSelect}
                  name="floor_type"
                  value={formData.floor_type}
                  onChange={handleChange}
                >
                  <option value="new">חדש</option>
                  <option value="existing">קיים</option>
                </select>
                {errors.floor_type && (
                  <div className={modalStyles.fieldError}>{errors.floor_type}</div>
                )}
              </div>

              <div className={modalStyles.field}>
                <label className={modalStyles.fieldLabel}>מספר מדרגות לקומה</label>
                <input
                  type="number"
                  className={modalStyles.fieldInput}
                  name="stairs_to_floor"
                  value={formData.stairs_to_floor}
                  onChange={handleChange}
                />
                {errors.stairs_to_floor && (
                  <div className={modalStyles.fieldError}>{errors.stairs_to_floor}</div>
                )}
              </div>
            </div>

            <div className={modalStyles.formRow}>
              <div className={modalStyles.field}>
                <label className={modalStyles.fieldLabel}>סגנון התקרה</label>
                <select
                  className={modalStyles.fieldSelect}
                  name="ceiling_style"
                  value={formData.ceiling_style}
                  onChange={handleChange}
                >
                  <option value="flat">שטוח</option>
                  <option value="drop">מונמך</option>
                  <option value="sloped">משופע</option>
                  <option value="vaulted">מקומר</option>
                </select>
                {errors.ceiling_style && (
                  <div className={modalStyles.fieldError}>{errors.ceiling_style}</div>
                )}
              </div>

              <div className={modalStyles.field}>
                <label className={modalStyles.fieldLabel}>חומר התקרה</label>
                <select
                  className={modalStyles.fieldSelect}
                  name="ceiling_material"
                  value={formData.ceiling_material}
                  onChange={handleChange}
                >
                  <option value="concrete">בטון</option>
                  <option value="wooden beams">קורות עץ</option>
                  <option value="exposed">חשוף</option>
                  <option value="plaster">טיח</option>
                </select>
                {errors.ceiling_material && (
                  <div className={modalStyles.fieldError}>{errors.ceiling_material}</div>
                )}
              </div>
            </div>
            {/* Floor-specific choices */}
            {choiceItemsWithChildren.length > 0 && (
              <div className={modalStyles.section}>
                <h3 className={modalStyles.sectionTitle}>בחירות לקומה</h3>
                {choiceItemsWithChildren.map(item => (
                  <div key={item.masterItemId} className={modalStyles.field}>
                    <label className={modalStyles.fieldLabel}>{item.name}</label>
                    <select
                      className={modalStyles.fieldSelect}
                      value={floorChoices[item.masterItemId] || ''}
                      onChange={e =>
                        setFloorChoices(prev => ({
                          ...prev,
                          [item.masterItemId]: e.target.value || null,
                        }))
                      }
                    >
                      <option value="">בחר...</option>
                      {item.children.map(child => (
                        <option key={child.id} value={child.id}>
                          {child.item_name}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={modalStyles.footer}>
          <button onClick={onClose} className={modalStyles.cancelButton}>
            ביטול
          </button>
          <button onClick={handleSubmit} className={modalStyles.primaryButton}>
            {floor ? 'שמור שינויים' : 'הוסף קומה'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default FloorFormModal;
